import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  IconButton,
  Modal,
  SwipeableDrawer,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  closeDisabled?: boolean;
  containerStyle?: SxProps<Theme>;
  contentStyle?: SxProps<Theme>;
  hideHeader?: boolean;
  mobileAnchor?: 'left' | 'right' | 'top' | 'bottom';
};

export default function ResponsibleModal({
  open,
  onClose,
  children,
  title,
  description,
  closeDisabled,
  containerStyle,
  contentStyle,
  hideHeader,
  mobileAnchor = 'right',
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isBottomSheet = isMobile && mobileAnchor === 'bottom';

  const content = (
    <Box
      sx={{
        borderRadius: isBottomSheet ? '16px 16px 0 0' : isMobile ? 0 : 1.5,
        maxHeight: isMobile ? '100%' : '90%',
        width: isMobile ? '100%' : 600,
        overflow: 'hidden',
        ...(isMobile
          ? { position: 'relative' }
          : {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }),
        ...containerStyle,
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: isBottomSheet ? '16px 16px 0 0' : isMobile ? 0 : 1.5,
          maxHeight: isMobile ? '100vh' : '90vh',
          px: isMobile ? 2 : 3.75,
          py: isMobile ? 2 : 3.75,
          width: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          ...contentStyle,
        }}
      >
        {!hideHeader ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {title ? <Typography variant="h1">{title}</Typography> : null}

            <IconButton
              onClick={onClose}
              sx={{ p: 0 }}
              disabled={closeDisabled}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}

        {description ? (
          <Typography variant="body1" color="text.secondary" mb={3}>
            {description}
          </Typography>
        ) : null}

        {children}
      </Box>
    </Box>
  );

  return isMobile ? (
    <SwipeableDrawer
      anchor={mobileAnchor}
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen={isBottomSheet}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: isBottomSheet
          ? {
              width: '100%',
              maxHeight: '92%',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }
          : { width: '90%' },
      }}
    >
      {content}
    </SwipeableDrawer>
  ) : (
    <Modal open={open} onClose={onClose}>
      {content}
    </Modal>
  );
}
